/**
 * Estado del carrito.
 *
 * Es un reductor puro: recibe una acción y devuelve un estado nuevo. Eso hace
 * que las reglas del carrito (no vender cantidades negativas, agrupar líneas del
 * mismo producto, repartir el descuento) se puedan probar sin montar una
 * pantalla, y que el comportamiento sea el mismo con ratón, con teclado o con
 * lector de barras.
 */

export interface CartLine {
  variantId: string;
  sku: string;
  name: string;
  uomCode: string | null;
  unitPriceCents: number;
  quantity: number;
  discountCents: number;
  tax: { code: string; rate: number; satCode: string; satFactor: string } | null;
  tracked: boolean;
  /** De dónde salió el precio, para poder explicarlo después. */
  priceSource: string;
}

export interface CartState {
  lines: CartLine[];
  globalDiscountCents: number;
  /** Línea seleccionada: es donde actúan los atajos de teclado. */
  selectedIndex: number;
}

export const emptyCart: CartState = { lines: [], globalDiscountCents: 0, selectedIndex: 0 };

export type CartAction =
  | { type: "add"; line: Omit<CartLine, "quantity" | "discountCents">; quantity?: number }
  | { type: "increment"; index: number; by?: number }
  | { type: "decrement"; index: number }
  | { type: "setQuantity"; index: number; quantity: number }
  | { type: "remove"; index: number }
  | { type: "lineDiscount"; index: number; percent: number }
  | { type: "globalDiscount"; percent: number }
  | { type: "select"; index: number }
  | { type: "move"; delta: number }
  | { type: "clear" };

/** Máximo descuento por línea, como proporción del bruto. */
export const MAX_DISCOUNT = 0.5;

function clampSelection(state: CartState): CartState {
  const max = Math.max(0, state.lines.length - 1);
  return { ...state, selectedIndex: Math.min(Math.max(0, state.selectedIndex), max) };
}

export function cartReducer(state: CartState, action: CartAction): CartState {
  switch (action.type) {
    case "add": {
      const quantity = action.quantity ?? 1;
      const existing = state.lines.findIndex((line) => line.variantId === action.line.variantId);
      if (existing >= 0) {
        // Vender el mismo producto dos veces suma cantidad en la misma línea:
        // el ticket impreso no repite el producto.
        const lines = state.lines.map((line, index) =>
          index === existing ? { ...line, quantity: line.quantity + quantity } : line,
        );
        return { ...state, lines, selectedIndex: existing };
      }
      return {
        ...state,
        lines: [...state.lines, { ...action.line, quantity, discountCents: 0 }],
        selectedIndex: state.lines.length,
      };
    }

    case "increment": {
      const by = action.by ?? 1;
      const lines = state.lines.map((line, index) =>
        index === action.index ? { ...line, quantity: line.quantity + by } : line,
      );
      return { ...state, lines };
    }

    case "decrement": {
      const target = state.lines[action.index];
      if (!target) return state;
      // Bajar de 1 quita la línea: es lo que espera cualquiera.
      if (target.quantity <= 1) return cartReducer(state, { type: "remove", index: action.index });
      const lines = state.lines.map((line, index) =>
        index === action.index ? { ...line, quantity: line.quantity - 1 } : line,
      );
      return { ...state, lines };
    }

    case "setQuantity": {
      if (action.quantity <= 0) return cartReducer(state, { type: "remove", index: action.index });
      const lines = state.lines.map((line, index) =>
        index === action.index
          ? { ...line, quantity: Math.round(action.quantity * 1000) / 1000 }
          : line,
      );
      return { ...state, lines };
    }

    case "remove": {
      const lines = state.lines.filter((_, index) => index !== action.index);
      return clampSelection({ ...state, lines, selectedIndex: action.index - 1 });
    }

    case "lineDiscount": {
      const line = state.lines[action.index];
      if (!line) return state;
      const percent = Math.min(Math.max(0, action.percent), MAX_DISCOUNT * 100);
      const gross = Math.round(line.unitPriceCents * line.quantity);
      const discountCents = Math.round((gross * percent) / 100);
      const lines = state.lines.map((item, index) =>
        index === action.index ? { ...item, discountCents } : item,
      );
      return { ...state, lines };
    }

    case "globalDiscount": {
      const percent = Math.min(Math.max(0, action.percent), MAX_DISCOUNT * 100);
      const gross = state.lines.reduce(
        (sum, line) => sum + Math.round(line.unitPriceCents * line.quantity),
        0,
      );
      return { ...state, globalDiscountCents: Math.round((gross * percent) / 100) };
    }

    case "select":
      return clampSelection({ ...state, selectedIndex: action.index });

    case "move":
      return clampSelection({ ...state, selectedIndex: state.selectedIndex + action.delta });

    case "clear":
      return emptyCart;

    /* c8 ignore next 2 -- el switch es exhaustivo */
    default:
      return state;
  }
}

/** ¿Se puede cobrar? Un carrito vacío no se cobra. */
export function canCharge(state: CartState): boolean {
  return state.lines.length > 0 && state.lines.every((line) => line.quantity > 0);
}
